import { selectCalculateDrugWaste } from './case-props'
import { createSelector } from '../utils/selector-utils'
import {
  selectParenteralDosesAdministeredByDrugId,
  selectOralSedativeDosesAdministeredByDrugId,
  selectSyringeFlushesBySiteId,
} from './drugs-administered'
import { selectSelectedInfusionDrugs } from './infusion-drug'
import { selectCaseOralSedatives } from './oral-sedative'
import { eventSelectors } from './events'
import { selectCaseParenDrugs, selectParenDrugDosage } from './paren-drug'
import { cleanUpRound } from '../utils/numbers'
import { addDiffUnits } from '../utils/add-diff-units'
import { selectUsingDetailedFluids, selectIvSites } from './fluids'
import { isNumber } from '../utils/is'

const totals = (doses, expectedRoutes) => {
  const byRoute = expectedRoutes.reduce((res, route) => {
    res[route] = 0
    return res
  }, {})
  const result = { total: 0, byRoute }
  let max = 0
  for (const { route, amount, time } of doses) {
    if (!result.byRoute.hasOwnProperty(route) || !amount || !time) {
      continue
    }
    max = Math.max(max, amount)
    byRoute[route] = cleanUpRound(byRoute[route] + amount)
    result.total = cleanUpRound(result.total + amount)
  }
  result.maxDose = max
  return result
}

const createArrayVersion = selector =>
  createSelector(selector, totals => {
    if (!totals) {
      return []
    }
    const asArray = Object.keys(totals).map(id => ({ id, ...totals[id] }))
    asArray.sort((a, b) => a.caseCount - b.caseCount)
    return asArray
  })

const createUsedVersion = selector =>
  createSelector(selector, totals => {
    const result = {}
    for (const key in totals) {
      const entry = totals[key]
      if (entry.total || entry.wasted) {
        result[key] = entry
      }
    }
    return result
  })

const parenRoutes = ['IV', 'IM', 'IN']
/** @type {CaseSelector<SingleCaseParenDrugTotalsById>} */
export const selectParenDrugTotalsNoInfusion = createSelector(
  selectCaseParenDrugs,
  selectCaseOralSedatives,
  eventSelectors.selectCaseStartTime,
  selectParenteralDosesAdministeredByDrugId,
  selectParenDrugDosage,
  selectCalculateDrugWaste,
  /**
   * @param {CaseData['parenDrugs']} parenDrugs
   * @param {CaseData['oralSedatives']} oralSedatives
   * @param {number | null} caseStartTime
   * @param {any} dosesByDrugId
   * @param {{ [drugId: string]: ParenDrugDosageProcessed }} dosageById
   * @param {boolean} calculateDrugWaste
   */
  (
    parenDrugs,
    oralSedatives,
    caseStartTime,
    dosesByDrugId,
    dosageById,
    calculateDrugWaste
  ) => {
    const result = {}
    for (const drugId in dosesByDrugId) {
      const allDoses = dosesByDrugId[drugId]
      const dosage = dosageById[drugId] || {}
      const { unit, name } = parenDrugs[drugId] || oralSedatives[drugId] || {}
      const doses = []
      let preProcedureTotal = 0
      allDoses.forEach(dose => {
        const { time } = dose
        if (!caseStartTime || caseStartTime > time) {
          preProcedureTotal += dose.amount
        } else {
          doses.push(dose)
        }
      })
      const loopReturn = (result[drugId] = totals(allDoses, parenRoutes))
      if (calculateDrugWaste) {
        loopReturn.wasted = cleanUpRound(
          // we have to add back pre-procedure total here because
          // we don't currently track waste for pre-procedure IM drugs
          // @ts-ignore
          (dosage.totalDrawn || 0) - loopReturn.total + preProcedureTotal
        )
      } else {
        // @ts-ignore
        loopReturn.wasted = dosage.wasted || 0
      }
      loopReturn.unit = unit || 'mg'
      loopReturn.name = name || null
      loopReturn.expended = cleanUpRound(loopReturn.total + loopReturn.wasted)
    }
    return result
  }
)
/** @type {CaseSelector<SingleCaseParenDrugTotalsById>} */
export const selectUsedParenDrugTotalsNoInfusion = createUsedVersion(
  selectParenDrugTotalsNoInfusion
)

/** @type {CaseSelector<SingleCaseParenDrugTotalsById>} */
export const selectInfusionDrugTotals = createSelector(
  selectSelectedInfusionDrugs,
  /** @param {SelectedInfusionDrug[]} infusionDrugs */
  infusionDrugs => {
    const result = {}
    for (const drug of infusionDrugs) {
      const { id, name, wasted, totalUnit, used, wasteConfirmed } = drug

      if (!wasteConfirmed || !totalUnit) {
        continue
      }

      let finalWasted = wasted || 0
      let finalTotal = used || 0

      result[id] = {
        wasted: finalWasted,
        unit: totalUnit,
        total: finalTotal,
        name,
        expended: cleanUpRound(finalTotal + finalWasted),
      }
    }
    return result
  }
)
/** @type {CaseSelector<SingleCaseParenDrugTotalsById>} */
export const selectUsedInfusionDrugTotals = createUsedVersion(
  selectInfusionDrugTotals
)

/** @type {(caseData: CaseData) => SingleCaseParenDrugTotalsById} */
export const selectParenDrugTotals = createSelector(
  selectParenDrugTotalsNoInfusion,
  selectInfusionDrugTotals,
  /**
   * @param {SingleCaseSingleParenDrugTotal} parenTotals
   * @param {SingleCaseSingleParenDrugTotal} infusionTotals
   * @returns {SingleCaseParenDrugTotalsById}
   */
  (parenTotals, infusionTotals) => {
    /** @type {SingleCaseParenDrugTotalsById} */
    const result = JSON.parse(JSON.stringify(parenTotals))
    for (const id in infusionTotals) {
      if (result[id]) {
        const paren = result[id]
        const infusion = infusionTotals[id]
        paren.total = addDiffUnits(
          paren.total,
          paren.unit,
          infusion.total,
          infusion.unit,
          paren.unit
        ).amount
        paren.byRoute.IV = addDiffUnits(
          paren.byRoute.IV,
          paren.unit,
          infusion.total,
          infusion.unit,
          paren.unit
        ).amount
        paren.wasted = addDiffUnits(
          paren.wasted,
          paren.unit,
          infusion.wasted,
          infusion.unit,
          paren.unit
        ).amount
        paren.expended = addDiffUnits(
          paren.expended,
          paren.unit,
          infusion.expended,
          infusion.unit,
          paren.unit
        ).amount
      } else {
        const entry = infusionTotals[id]
        result[id] = entry
        // we do this to keep it consistent
        // structure for infusions as for other
        // paren drugs
        result[id].byRoute = {
          IV: entry.total,
          IM: 0,
          IN: 0,
        }
      }
    }

    return result
  }
)
/** @type {(caseData: CaseData) => SingleCaseParenDrugTotalsById} */
export const selectUsedParenDrugTotals = createUsedVersion(
  selectParenDrugTotals
)

const oralRoutes = ['PO', 'SL']
/** @type {CaseSelector<SingleCaseOralDrugTotalsById>} */
export const selectOralSedativeTotals = createSelector(
  selectCaseOralSedatives,
  selectOralSedativeDosesAdministeredByDrugId,
  /**
   * @type {(
   *   oralSedatives: CaseData['oralSedatives'],
   *   oralDoses: ReturnType<selectOralSedativeDosesAdministeredByDrugId>
   * ) => SingleCaseOralDrugTotalsById}
   */
  (oralSedatives, oralDoses) => {
    /** @type {SingleCaseOralDrugTotalsById} */
    const result = {}
    for (const drugId in oralDoses) {
      const doses = oralDoses[drugId]
      const { unit, name } = oralSedatives[drugId] || {}
      /** @type {SingleCaseSingleOralDrugTotal} */
      // @ts-ignore
      const loopReturn = (result[drugId] = totals(doses, oralRoutes))
      // @ts-ignore
      loopReturn.unit = unit || 'mg'
      loopReturn.name = name || null
    }

    return result
  }
)
/** @type {CaseSelector<SingleCaseOralDrugTotalsById>} */
export const selectUsedOralSedativeTotals = createUsedVersion(
  selectOralSedativeTotals
)

/** @type {CaseSelector<SingleCaseSingleOralDrugTotal[]>} */
export const selectOralSedativeTotalsAsArray = createArrayVersion(
  selectOralSedativeTotals
)
/** @type {CaseSelector<SingleCaseSingleOralDrugTotal[]>} */
export const selectUsedOralSedativeTotalsAsArray = createArrayVersion(
  selectUsedOralSedativeTotals
)
/** @type {CaseSelector<SingleCaseSingleParenDrugTotal[]>} */
export const selectParenDrugTotalsAsArray = createArrayVersion(
  selectParenDrugTotals
)
/** @type {CaseSelector<SingleCaseSingleParenDrugTotal[]>} */
export const selectUsedParenDrugTotalsAsArray = createArrayVersion(
  selectUsedParenDrugTotals
)
/** @type {CaseSelector<FluidTotals>} */
export const selectSolutionFluidTotals = createSelector(
  selectIvSites,
  selectUsingDetailedFluids,
  selectSyringeFlushesBySiteId,
  /**
   * @param {IvSite[]} ivSites
   * @returns {FluidTotals}
   */
  (ivSites, usingDetailedFluids, syringeFlushesBySiteId) => {
    const interim = { overall: {}, bySite: {} }
    const addTotal = (targetObj, name, total) => {
      if (!targetObj[name]) {
        targetObj[name] = { id: name, name, total, unit: 'mL' }
      } else {
        targetObj[name].total += total
      }
    }
    for (const site of ivSites) {
      const siteTotals = {}
      const { solutions } = site
      const syringeFlushes = syringeFlushesBySiteId[site.id] || []
      if (solutions) {
        for (const solution of solutions) {
          const { name, amount, amountConfirmed } = solution
          if (
            !name ||
            name === 'None' ||
            !isNumber(amount) ||
            (usingDetailedFluids && !amountConfirmed)
          ) {
            continue
          }
          addTotal(interim.overall, name, amount)
          addTotal(siteTotals, name, amount)
        }
      }
      for (const flush of syringeFlushes) {
        const { drugId, amount } = flush
        if (!drugId || !isNumber(amount)) {
          continue
        }
        addTotal(interim.overall, drugId, amount)
        addTotal(siteTotals, drugId, amount)
      }
      interim.bySite[site.id] = siteTotals
    }

    /** @type {FluidTotals} */
    const toReturn = { overall: [], bySite: {} }
    for (const key in interim.overall) {
      toReturn.overall.push(interim.overall[key])
    }

    for (const key in interim.bySite) {
      const siteTotals = interim.bySite[key]
      toReturn.bySite[key] = []
      for (const siteKey in siteTotals) {
        toReturn.bySite[key].push(siteTotals[siteKey])
      }
    }

    return toReturn
  }
)

/** @type {CaseSelector<FluidTotal[]>} */
export const selectSolutionFluidTotalsAsArray = createSelector(
  selectSolutionFluidTotals,
  totals => totals.overall
)
